'use client';

import { useEffect, useRef, useState } from 'react';
import { useSession } from 'next-auth/react';
import { Add } from '@mui/icons-material';
import {
  Box,
  Button,
  FormControlLabel,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import {
  StyledCampaignFormLabelTitle,
  StyledCampaignFormRow,
  useBreadcrumbs,
} from '~/entities';
import { ActiveCampaignRow } from '~/entities/ActivesCampaigns';
import { downloadActiveCampaignsExport } from '~/entities/ActivesCampaigns/hooks';
import { useGetActiveCampaigns } from '~/entities/ActivesCampaigns/hooks/useGetActiveCampaigns';
import {
  ContentTitle,
  DefaultSwitch,
  getHeaderString,
  StyledContainer,
  StyledHeaderTableCell,
  StyledTable,
  StyledTableContainer,
} from '~/shared';

import { LoadingActivesCampaignsView } from './loading';

export function ActiveCampaignsView() {
  const breadcrumbs = useBreadcrumbs();
  const session = useSession();
  const manufacturerId = session?.data?.user?.manufacturerId;

  const { data: camp = [], isLoading } = useGetActiveCampaigns(manufacturerId);

  const [campaignActive, setCampaignActive] = useState<
    { id: number; active: boolean }[]
  >([]);

  // const session = useSession();
  // const token = session?.data?.t.st;

  const hasInitialized = useRef(false);
  useEffect(() => {
    if (!hasInitialized.current) {
      breadcrumbs.setPaths([
        { label: 'Campanhas Ativas', path: '/campanhas-ativas' },
      ]);
      hasInitialized.current = true;
    }
  }, [breadcrumbs]);

  useEffect(() => {
    if (camp.length > 0) {
      setCampaignActive(
        Array.from({ length: camp.length }, (_, index) => ({
          id: index,
          active: false,
        }))
      );
    }
  }, [camp]);

  const sortedCamp = camp.sort((a, b) => {
    const hasBannersA = a.banners.length > 0;
    const hasBannersB = b.banners.length > 0;

    if (hasBannersA && !hasBannersB) {
      return -1;
    }

    if (!hasBannersA && hasBannersB) {
      return 1;
    }

    return 0;
  });

  const handleDownload = async () => {
    try {
      const response = await downloadActiveCampaignsExport();

      const blob = new Blob([response.data], {
        type: getHeaderString(response.headers['content-type']),
      });
      const downloadUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = 'ActiveCampaigns-Export';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <>
      <ContentTitle
        title="Campanhas Ativas"
        actions={
          <Button
            variant="contained"
            color="primary"
            size="medium"
            onClick={() => handleDownload()}
            sx={theme => ({
              textTransform: 'none',
              py: theme.spacing(1),
              px: theme.spacing(2),
              [theme.breakpoints.down('lg')]: {
                py: theme.spacing(3),
                fontSize: theme.typography.pxToRem(18),
                width: '90%',
                mx: 'auto',
              },
            })}
            startIcon={<Add />}
          >
            Baixar relatório
          </Button>
        }
        isFetching={isLoading}
      />
      {!isLoading ? (
        <StyledContainer>
          {sortedCamp.map((campaign, index) => (
            <Box key={campaign?.formatName} sx={{ marginBottom: 3 }}>
              <StyledCampaignFormRow key={campaign?.formatName}>
                <FormControlLabel
                  control={<DefaultSwitch />}
                  label={
                    <StyledCampaignFormLabelTitle sx={{ display: 'flex' }}>
                      {`${campaign?.formatName}`}
                      {campaign.banners.length < 1 && (
                        <Typography sx={{ color: 'red' }}>
                          (Não há campanhas)
                        </Typography>
                      )}
                    </StyledCampaignFormLabelTitle>
                  }
                  checked={campaignActive[index]?.active}
                  onChange={(_, checked) =>
                    setCampaignActive(prev =>
                      prev.map(item =>
                        item.id === index ? { ...item, active: checked } : item
                      )
                    )
                  }
                />
              </StyledCampaignFormRow>
              {campaignActive[index]?.active && (
                <StyledTableContainer>
                  <StyledTable>
                    <TableHead>
                      <TableRow>
                        <StyledHeaderTableCell
                          sx={{ width: '60px' }}
                          align="center"
                        >
                          ID
                        </StyledHeaderTableCell>
                        <StyledHeaderTableCell
                          sx={{
                            minWidth: '200px',
                            position: 'sticky',
                            left: 0,
                            backgroundColor: 'background.paper',
                          }}
                        >
                          Anunciante / Campanha
                        </StyledHeaderTableCell>
                        <StyledHeaderTableCell align="center">
                          Plataforma
                        </StyledHeaderTableCell>
                        <StyledHeaderTableCell align="center">
                          Investimento
                        </StyledHeaderTableCell>
                        <StyledHeaderTableCell align="center">
                          Início
                        </StyledHeaderTableCell>
                        <StyledHeaderTableCell align="center">
                          Término
                        </StyledHeaderTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {campaign?.banners?.length === 0 &&
                        !campaign.banners[0]?.campaignId && (
                          <TableRow>
                            <TableCell colSpan={7} align="center">
                              Sem Banners
                            </TableCell>
                          </TableRow>
                        )}
                      {campaign?.banners.map(banner => (
                        <ActiveCampaignRow
                          key={banner.campaignId}
                          banner={banner}
                        />
                      ))}
                    </TableBody>
                  </StyledTable>
                </StyledTableContainer>
              )}
            </Box>
          ))}
        </StyledContainer>
      ) : (
        <LoadingActivesCampaignsView />
      )}
    </>
  );
}
